<template>
  <div class="operator-settings">
      <div v-loading="profileLoading" class="identity-bar">
        <div v-if="currentProfile" class="identity-content">
          <div class="identity-heading">
            <span class="identity-label">当前登录身份</span>
            <span class="identity-name">{{ currentProfile.userName || '未设置姓名' }}</span>
            <el-tag
              :type="currentProfile.provisioned && currentProfile.enabled ? 'success' : 'warning'"
              size="mini"
            >
              {{ currentProfile.provisioned
                ? (currentProfile.enabled ? '运营代理已开通' : '运营代理已停用')
                : '尚未开通运营代理' }}
            </el-tag>
          </div>
        </div>
        <div v-else class="identity-content">
          <div class="identity-label">当前登录身份</div>
          <div class="secondary-text">{{ profileError || '正在读取当前登录身份' }}</div>
        </div>
        <div class="identity-actions">
          <el-button
            v-if="currentProfile && !currentProfile.provisioned"
            type="primary"
            size="small"
            @click="openCreateCurrentCaller"
          >为当前账号开通</el-button>
          <el-button
            v-if="profileError"
            size="small"
            @click="loadCurrentProfile(false)"
          >重新读取</el-button>
        </div>
      </div>

      <div class="summary-grid">
        <div class="summary-item">
          <div class="summary-label">已授权平台</div>
          <div class="summary-value">{{ authorizedPlatformCount }}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">运营账号</div>
          <div class="summary-value">{{ callers.length }}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">已启用账号</div>
          <div class="summary-value">{{ enabledCallerCount }}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">当前累计调用</div>
          <div class="summary-value">{{ totalChargedCount }}</div>
        </div>
      </div>

      <el-tabs v-model="section" class="operator-tabs">
        <el-tab-pane label="运营账号与权限" name="callers">
          <div class="toolbar">
            <div class="filters">
              <el-select v-model="callerFilter.enabled" clearable placeholder="全部状态" class="filter-status">
                <el-option label="已启用" :value="true"/>
                <el-option label="已停用" :value="false"/>
              </el-select>
              <el-button :loading="callerLoading" @click="loadCallers">查询</el-button>
            </div>
            <el-button type="primary" icon="el-icon-plus" @click="openCreateCaller">新增运营账号</el-button>
          </div>

          <el-table
            v-loading="callerLoading"
            :data="callers"
            empty-text="当前筛选条件下没有运营账号"
          >
            <el-table-column label="运营账号" min-width="180">
              <template slot-scope="{ row }">
                <div class="primary-text">{{ row.userName }}</div>
              </template>
            </el-table-column>
            <el-table-column label="授权平台" min-width="250">
              <template slot-scope="{ row }">
                <el-tag
                  v-for="platform in row.authorizedPlatforms"
                  :key="platform"
                  size="mini"
                  class="platform-tag"
                >{{ platformLabel(platform) }}</el-tag>
                <span v-if="!row.authorizedPlatforms || !row.authorizedPlatforms.length" class="secondary-text">未授权平台</span>
              </template>
            </el-table-column>
            <el-table-column label="今日额度" width="130">
              <template slot-scope="{ row }">
                {{ usageText(row.dailyUsedCount, row.dailyReservedCount, row.dailyLimit) }}
              </template>
            </el-table-column>
            <el-table-column label="累计额度" width="150">
              <template slot-scope="{ row }">
                {{ usageText(row.totalUsedCount, row.totalReservedCount, row.totalLimit) }}
              </template>
            </el-table-column>
            <el-table-column label="状态" width="90">
              <template slot-scope="{ row }">
                <el-tag :type="row.enabled ? 'success' : 'info'" size="small">
                  {{ row.enabled ? '已启用' : '已停用' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="150" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="text" @click="openEditCaller(row)">编辑</el-button>
                <el-button type="text" @click="resetQuota(row)">重置额度</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="调用记录" name="records">
          <div class="record-filters">
            <el-select
              v-model="recordFilter.userId"
              clearable
              filterable
              placeholder="全部运营人员"
            >
              <el-option
                v-for="caller in callers"
                :key="caller.userId"
                :label="caller.userName"
                :value="caller.userId"
              />
            </el-select>
            <el-select v-model="recordFilter.platformCode" clearable placeholder="全部平台">
              <el-option
                v-for="platform in platformOptions"
                :key="platform.code"
                :label="platform.name"
                :value="platform.code"
              />
            </el-select>
            <el-select
              v-model="recordFilter.operationCode"
              clearable
              filterable
              placeholder="全部接口能力"
              class="operation-filter"
            >
              <el-option
                v-for="endpoint in filteredEndpointOptions"
                :key="endpoint.operationCode"
                :label="endpoint.operationName || endpoint.operationCode"
                :value="endpoint.operationCode"
              />
            </el-select>
            <el-select v-model="recordFilter.resultStatus" clearable placeholder="全部结果">
              <el-option label="成功" value="SUCCESS"/>
              <el-option label="失败" value="FAILED"/>
              <el-option label="超时" value="TIMEOUT"/>
            </el-select>
            <el-date-picker
              v-model="recordDateRange"
              type="datetimerange"
              value-format="yyyy-MM-dd HH:mm:ss"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              range-separator="至"
            />
            <el-button type="primary" :loading="recordLoading" @click="loadRecords">查询</el-button>
          </div>
          <el-table
            v-loading="recordLoading"
            :data="records"
            empty-text="当前筛选条件下没有调用记录"
          >
            <el-table-column prop="startedAt" label="调用时间" width="170">
              <template slot-scope="{ row }">{{ dateTime(row.startedAt) }}</template>
            </el-table-column>
            <el-table-column label="运营人员" min-width="180">
              <template slot-scope="{ row }">
                <div class="primary-text">{{ row.userName || '未知运营人员' }}</div>
              </template>
            </el-table-column>
            <el-table-column label="平台" width="100">
              <template slot-scope="{ row }">{{ platformLabel(row.platformCode) }}</template>
            </el-table-column>
            <el-table-column label="接口能力" min-width="210">
              <template slot-scope="{ row }">
                <div class="primary-text">{{ operationLabel(row.operationCode) }}</div>
                <div class="secondary-text">{{ row.operationCode }}</div>
              </template>
            </el-table-column>
            <el-table-column label="结果" width="90">
              <template slot-scope="{ row }">
                <el-tag :type="resultType(row.resultStatus)" size="small">
                  {{ resultLabel(row.resultStatus) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="计次结论" min-width="170">
              <template slot-scope="{ row }">
                <el-tag :type="number(row.chargedCount) > 0 ? 'warning' : 'info'" size="mini">
                  {{ number(row.chargedCount) > 0 ? `计${number(row.chargedCount)}次` : '不计次' }}
                </el-tag>
                <div class="secondary-text count-reason">{{ countReason(row) }}</div>
              </template>
            </el-table-column>
            <el-table-column label="失败原因" min-width="190">
              <template slot-scope="{ row }">
                <div>{{ errorReason(row.errorCode) }}</div>
                <div v-if="row.errorCode" class="secondary-text">{{ row.errorCode }}</div>
              </template>
            </el-table-column>
            <el-table-column label="耗时" width="90">
              <template slot-scope="{ row }">{{ number(row.elapsedMs) }}ms</template>
            </el-table-column>
            <el-table-column label="操作" width="70" fixed="right">
              <template slot-scope="{ row }">
                <el-button type="text" @click="openRecordDetail(row)">查看</el-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane label="代理接口状态" name="endpoints">
          <div class="endpoint-sync-bar">
            <div>
              <div class="endpoint-sync-title">内置接口目录</div>
              <div class="secondary-text">
                当前已录入 {{ endpointCatalogCount }} / {{ endpointBuiltinCount }} 项。
                <template v-if="endpointSyncRequired">
                  缺少 {{ missingEndpointCount }} 项，契约待更新 {{ outdatedEndpointCount }} 项。
                </template>
                <template v-else>代码能力与数据库目录一致。</template>
              </div>
            </div>
            <el-button
              :type="endpointSyncRequired ? 'primary' : 'default'"
              :loading="endpointSyncing"
              :disabled="endpointLoading || Boolean(endpointSaving)"
              @click="syncBuiltinEndpoints"
            >{{ endpointSyncing ? '同步中' : '同步内置接口' }}</el-button>
          </div>
          <el-alert
            class="endpoint-note"
            :type="endpointSyncRequired ? 'warning' : 'info'"
            :closable="false"
            show-icon
            :title="endpointSyncRequired
              ? '检测到代码已支持但数据库尚未录入或契约已变化的接口，请先同步内置接口。'
              : '先使用真实参数完成冒烟并人工核对结果，再标记验证通过和启用。冒烟会按一次真实 TikHub 调用计次。'"
          />
          <el-table
            v-loading="endpointLoading"
            :data="endpoints"
            empty-text="没有读取到代理接口目录"
          >
            <el-table-column label="平台" width="105">
              <template slot-scope="{ row }">{{ platformLabel(row.platformCode) }}</template>
            </el-table-column>
            <el-table-column prop="operationName" label="接口名称" min-width="170"/>
            <el-table-column prop="operationCode" label="能力编码" min-width="220" show-overflow-tooltip/>
            <el-table-column label="验证状态" width="120">
              <template slot-scope="{ row }">
                <el-tag :type="liveStatusType(row.liveStatus)" size="small">
                  {{ liveStatusLabel(row.liveStatus) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="启用状态" width="100">
              <template slot-scope="{ row }">
                <el-switch
                  :value="Boolean(row.enabled)"
                  :disabled="row.liveStatus !== 'LIVE_VERIFIED' || endpointSaving === row.operationCode"
                  @change="updateEndpoint(row, { enabled: $event })"
                />
              </template>
            </el-table-column>
            <el-table-column label="操作" width="250" fixed="right">
              <template slot-scope="{ row }">
                <el-button
                  type="text"
                  :disabled="Boolean(endpointSaving)"
                  @click="openSmokeDialog(row)"
                >真实冒烟</el-button>
                <el-dropdown
                  :disabled="Boolean(endpointSaving)"
                  trigger="click"
                  @command="command => updateEndpoint(row, { live_status: command })"
                >
                  <el-button type="text" :loading="endpointSaving === row.operationCode">
                    更新验证状态<i class="el-icon-arrow-down el-icon--right"/>
                  </el-button>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item command="LIVE_VERIFIED">标记验证通过</el-dropdown-item>
                    <el-dropdown-item command="LIVE_FAILED">标记验证失败</el-dropdown-item>
                    <el-dropdown-item command="PENDING">恢复待验证</el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
    <el-dialog
      :title="editingCallerId ? '编辑运营权限' : '新增运营授权'"
      :visible.sync="callerDialogVisible"
      custom-class="caller-dialog"
      width="640px"
      append-to-body
      :close-on-click-modal="false"
      :show-close="!callerSaving"
      @closed="resetCallerForm"
    >
      <el-form
        ref="callerForm"
        :model="callerForm"
        :rules="callerRules"
        label-width="104px"
        :disabled="callerSaving"
      >
        <el-form-item label="运营人员" prop="userId">
          <div v-if="editingCallerId || creatingCurrentCaller" class="selected-user">
            <div class="primary-text">{{ callerForm.userName }}</div>
          </div>
          <el-select
            v-else
            v-model="callerForm.userId"
            filterable
            :loading="operatorUsersLoading"
            placeholder="按姓名搜索并选择运营人员"
            class="full-width"
            @visible-change="visible => visible && loadOperatorUsers(false)"
            @change="selectOperatorUser"
          >
            <el-option
              v-for="user in operatorUsers"
              :key="user.userId"
              :label="user.userName"
              :value="user.userId"
              :disabled="isCallerConfigured(user.userId)"
            >
              <div class="user-option">
                <span>{{ user.userName }}</span>
                <small v-if="isCallerConfigured(user.userId)">已授权</small>
              </div>
            </el-option>
          </el-select>
          <div class="form-help">人员来自乐纯账号系统，保存后其 API Key 会自动映射到这条授权。</div>
        </el-form-item>
        <el-form-item label="可用平台" prop="authorizedPlatforms">
          <el-checkbox-group v-model="callerForm.authorizedPlatforms">
            <el-checkbox
              v-for="platform in platformOptions"
              :key="platform.code"
              :label="platform.code"
            >{{ platform.name }}</el-checkbox>
          </el-checkbox-group>
          <div class="form-help">按平台授权，平台下已启用的接口均可调用。</div>
        </el-form-item>
        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="每日上限" prop="dailyLimit">
              <el-input-number
                v-model="callerForm.dailyLimit"
                :min="0"
                :max="2147483647"
                controls-position="right"
                class="full-width"
              />
              <div class="form-help">0表示不限次数。</div>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="累计上限" prop="totalLimit">
              <el-input-number
                v-model="callerForm.totalLimit"
                :min="0"
                :max="9007199254740991"
                controls-position="right"
                class="full-width"
              />
              <div class="form-help">从首次授权或最近一次重置起累计；0表示不限。</div>
            </el-form-item>
          </el-col>
        </el-row>
        <el-form-item label="授权状态">
          <el-switch
            v-model="callerForm.enabled"
            active-text="启用"
            inactive-text="停用"
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button :disabled="callerSaving" @click="callerDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="callerSaving" @click="saveCaller">
          {{ callerSaving ? '保存中' : '保存' }}
        </el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="调用记录详情"
      :visible.sync="recordDetailVisible"
      width="720px"
      append-to-body
    >
      <el-descriptions v-if="recordDetail" :column="2" border size="small">
        <el-descriptions-item label="运营人员">
          {{ recordDetail.userName || '未知运营人员' }}
        </el-descriptions-item>
        <el-descriptions-item label="平台">
          {{ platformLabel(recordDetail.platformCode) }}
        </el-descriptions-item>
        <el-descriptions-item label="接口能力" :span="2">
          {{ operationLabel(recordDetail.operationCode) }}
          <div class="secondary-text">{{ recordDetail.operationCode }}</div>
        </el-descriptions-item>
        <el-descriptions-item label="调用结果">
          <el-tag :type="resultType(recordDetail.resultStatus)" size="mini">
            {{ resultLabel(recordDetail.resultStatus) }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="计次结论">
          {{ number(recordDetail.chargedCount) > 0
            ? `计${number(recordDetail.chargedCount)}次`
            : '不计次' }}
        </el-descriptions-item>
        <el-descriptions-item label="计次依据" :span="2">
          {{ countReason(recordDetail) }}
        </el-descriptions-item>
        <el-descriptions-item label="额度处理">
          {{ quotaStatusLabel(recordDetail.quotaStatus) }}
        </el-descriptions-item>
        <el-descriptions-item label="TikHub HTTP">
          {{ recordDetail.tikhubHttpStatus || '-' }}
        </el-descriptions-item>
        <el-descriptions-item label="失败原因" :span="2">
          {{ errorReason(recordDetail.errorCode) }}
          <div v-if="recordDetail.errorCode" class="secondary-text">
            错误码：{{ recordDetail.errorCode }}
          </div>
        </el-descriptions-item>
        <el-descriptions-item label="调用时间">
          {{ dateTime(recordDetail.startedAt) }}
        </el-descriptions-item>
        <el-descriptions-item label="发往 TikHub">
          {{ dateTime(recordDetail.sentAt) }}
        </el-descriptions-item>
        <el-descriptions-item label="完成时间">
          {{ dateTime(recordDetail.completedAt) }}
        </el-descriptions-item>
        <el-descriptions-item label="调用耗时">
          {{ number(recordDetail.elapsedMs) }}ms
        </el-descriptions-item>
        <el-descriptions-item label="requestId" :span="2">
          {{ recordDetail.requestId || '-' }}
        </el-descriptions-item>
      </el-descriptions>
      <span slot="footer">
        <el-button @click="recordDetailVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="TikHub 接口真实冒烟"
      :visible.sync="smokeDialogVisible"
      width="760px"
      append-to-body
      :close-on-click-modal="false"
      :show-close="!smokeSubmitting"
      @closed="resetSmokeDialog"
    >
      <template v-if="smokeEndpoint">
        <el-descriptions :column="2" border size="small" class="smoke-summary">
          <el-descriptions-item label="接口名称">
            {{ smokeEndpoint.operationName }}
          </el-descriptions-item>
          <el-descriptions-item label="平台">
            {{ platformLabel(smokeEndpoint.platformCode) }}
          </el-descriptions-item>
          <el-descriptions-item label="能力编码" :span="2">
            {{ smokeEndpoint.operationCode }}
          </el-descriptions-item>
        </el-descriptions>

        <el-alert
          type="warning"
          :closable="false"
          show-icon
          title="这是一次真实 TikHub 请求"
          description="当前管理员必须已配置为启用的运营账号，并拥有该平台权限和剩余额度。请求发往 TikHub 后计1次，不自动重试。"
          class="smoke-warning"
        />

        <el-form label-position="top" :disabled="smokeSubmitting">
          <el-form-item label="请求参数（JSON）" :error="smokeParameterError">
            <el-input
              v-model="smokeParameterText"
              type="textarea"
              :rows="10"
              resize="vertical"
              spellcheck="false"
              placeholder="请根据 DATA_DICTIONARY.md 填写该接口的真实参数"
              @input="smokeParameterError = ''"
            />
            <div class="form-help">
              只填写接口文档中的业务参数；不要填写当前登录运营身份、API Key 或 TikHub Key。
              文档要求的目标平台 user_id 属于业务参数，可以填写。
            </div>
          </el-form-item>
        </el-form>

        <div v-if="smokeResult" class="smoke-result">
          <div class="smoke-result-title">
            <span>最近一次冒烟结果</span>
            <el-tag type="success" size="small">调用成功，已计1次</el-tag>
          </div>
          <div class="secondary-text">requestId：{{ smokeResult.requestId || '-' }}</div>
          <pre>{{ formattedSmokeResult }}</pre>
        </div>
        <div v-if="smokeError" class="smoke-result smoke-result--error">
          <div class="smoke-result-title">
            <span>最近一次冒烟失败</span>
            <el-tag type="danger" size="small">
              {{ smokeErrorChargedCount ? `调用失败，已计${smokeErrorChargedCount}次` : '调用失败，未计次' }}
            </el-tag>
          </div>
          <div class="secondary-text">requestId：{{ smokeError.requestId || '-' }}</div>
          <div class="smoke-error-message">{{ smokeError.message }}</div>
          <div v-if="smokeError.code" class="secondary-text">错误码：{{ smokeError.code }}</div>
          <div v-if="smokeError.providerHttpStatus" class="secondary-text">
            TikHub HTTP：{{ smokeError.providerHttpStatus }}
          </div>
          <div v-if="smokeError.providerMessage" class="smoke-provider-message">
            TikHub返回：{{ smokeError.providerMessage }}
          </div>
          <div v-if="smokeErrorChargedCount" class="smoke-retry-tip">
            本次已经计入调用次数。为避免重复计次，请关闭弹窗、核对参数后再重新发起。
          </div>
        </div>
      </template>

      <span slot="footer">
        <el-button :disabled="smokeSubmitting" @click="smokeDialogVisible = false">关闭</el-button>
        <el-button
          v-if="smokeResult"
          :disabled="smokeSubmitting"
          @click="markSmokeVerified"
        >结果已核对，标记验证通过</el-button>
        <el-button
          type="primary"
          :loading="smokeSubmitting"
          :disabled="smokeRetryBlocked"
          @click="submitSmoke"
        >
          {{ smokeSubmitText }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { yuqingApi } from '@/api/yuqing'

const ADMIN_BASE = 'tikhub/operator/admin'
const PLATFORM_NAMES = {
  bilibili: 'B站',
  douyin: '抖音',
  kuaishou: '快手',
  wechat_channels: '视频号',
  wechat_mp: '微信公众号',
  weibo: '微博',
  xiaohongshu: '小红书',
  zhihu: '知乎'
}

const emptyCaller = () => ({
  userId: '',
  userName: '',
  authorizedPlatforms: [],
  dailyLimit: 0,
  totalLimit: 0,
  enabled: true
})

export default {
  data () {
    return {
      section: 'callers',
      callers: [],
      records: [],
      endpoints: [],
      operatorUsers: [],
      callerLoading: false,
      recordLoading: false,
      endpointLoading: false,
      operatorUsersLoading: false,
      profileLoading: false,
      callerSaving: false,
      endpointSaving: '',
      endpointSyncing: false,
      endpointCatalogCount: 0,
      endpointBuiltinCount: 0,
      missingEndpoints: [],
      outdatedEndpoints: [],
      smokeDialogVisible: false,
      smokeSubmitting: false,
      smokeEndpoint: null,
      smokeParameterText: '{}',
      smokeParameterError: '',
      smokeResult: null,
      smokeError: null,
      currentProfile: null,
      profileError: '',
      callerFilter: { enabled: '' },
      recordDateRange: [],
      recordDetailVisible: false,
      recordDetail: null,
      recordFilter: {
        userId: '',
        platformCode: '',
        operationCode: '',
        resultStatus: ''
      },
      callerDialogVisible: false,
      editingCallerId: null,
      creatingCurrentCaller: false,
      callerForm: emptyCaller(),
      callerRules: {
        userId: [{ required: true, message: '请选择运营人员', trigger: 'change' }],
        authorizedPlatforms: [{
          type: 'array',
          required: true,
          min: 1,
          message: '请至少授权一个平台',
          trigger: 'change'
        }]
      }
    }
  },
  computed: {
    enabledCallerCount () {
      return this.callers.filter(item => item.enabled).length
    },
    totalChargedCount () {
      return this.callers.reduce((total, item) => total + this.number(item.totalUsedCount), 0)
    },
    authorizedPlatformCount () {
      const values = new Set()
      this.callers.forEach(item => {
        const platforms = item.authorizedPlatforms || []
        platforms.forEach(platform => values.add(platform))
      })
      return values.size
    },
    platformOptions () {
      const codes = new Set(Object.keys(PLATFORM_NAMES))
      this.endpoints.forEach(item => {
        if (item.platformCode) codes.add(item.platformCode)
      })
      return Array.from(codes)
        .map(code => ({ code, name: this.platformLabel(code) }))
        .sort((a, b) => a.name.localeCompare(b.name, 'zh-CN'))
    },
    filteredEndpointOptions () {
      if (!this.recordFilter.platformCode) return this.endpoints
      return this.endpoints.filter(item => item.platformCode === this.recordFilter.platformCode)
    },
    missingEndpointCount () {
      return Math.max(
        this.missingEndpoints.length,
        Math.max(0, this.endpointBuiltinCount - this.endpointCatalogCount)
      )
    },
    outdatedEndpointCount () {
      return this.outdatedEndpoints.length
    },
    endpointSyncRequired () {
      return this.endpointCatalogCount !== this.endpointBuiltinCount ||
        this.missingEndpointCount > 0 ||
        this.outdatedEndpointCount > 0
    },
    formattedSmokeResult () {
      if (!this.smokeResult) return ''
      return JSON.stringify(this.smokeResult.data, null, 2)
    },
    smokeErrorChargedCount () {
      return Number((this.smokeError && this.smokeError.usage &&
        this.smokeError.usage.chargedCount) || 0)
    },
    smokeRetryBlocked () {
      return !this.smokeSubmitting && this.smokeErrorChargedCount > 0
    },
    smokeSubmitText () {
      if (this.smokeSubmitting) return '调用中'
      if (this.smokeRetryBlocked) return '本次已计次，请关闭后重试'
      if (this.smokeResult) return '再次冒烟'
      return '确认发起冒烟'
    }
  },
  mounted () {
    this.loadAll()
  },
  methods: {
    async loadAll () {
      await Promise.all([
        this.loadCurrentProfile(true),
        this.loadCallers(true),
        this.loadEndpoints(true)
      ])
    },
    async loadCurrentProfile (silent) {
      this.profileLoading = true
      this.profileError = ''
      try {
        const response = await yuqingApi.get('tikhub/operator/me')
        this.currentProfile = response.data || null
        if (!this.currentProfile || !this.currentProfile.userId) {
          throw new Error('登录身份响应缺少userId')
        }
      } catch (error) {
        this.currentProfile = null
        this.profileError = error.message || '当前登录身份读取失败'
        if (!silent) this.$message.error(this.profileError)
      } finally {
        this.profileLoading = false
      }
    },
    handleLoadError (error, silent) {
      const message = error.message || '运营代理管理数据加载失败'
      if (!silent) this.$message.error(message)
    },
    async loadCallers (silent) {
      this.callerLoading = true
      try {
        const params = {}
        if (typeof this.callerFilter.enabled === 'boolean') params.enabled = this.callerFilter.enabled
        this.callers = (await yuqingApi.get(`${ADMIN_BASE}/callers`, params)).items || []
      } catch (error) {
        this.callers = []
        this.handleLoadError(error, silent === true)
      } finally {
        this.callerLoading = false
      }
    },
    async loadRecords () {
      this.recordLoading = true
      try {
        const params = { limit: 200 }
        Object.keys(this.recordFilter).forEach(key => {
          const value = this.recordFilter[key]
          if (value) {
            params[({
              userId: 'user_id',
              platformCode: 'platform_code',
              operationCode: 'operation_code',
              resultStatus: 'result_status'
            })[key]] = value
          }
        })
        if (this.recordDateRange && this.recordDateRange.length === 2) {
          params.started_after = this.recordDateRange[0]
          params.started_before = this.recordDateRange[1]
        }
        this.records = (await yuqingApi.get(`${ADMIN_BASE}/call-records`, params)).items || []
      } catch (error) {
        this.records = []
        this.handleLoadError(error, false)
      } finally {
        this.recordLoading = false
      }
    },
    openRecordDetail (row) {
      this.recordDetail = row
      this.recordDetailVisible = true
    },
    async loadEndpoints (silent) {
      this.endpointLoading = true
      try {
        const response = await yuqingApi.get(`${ADMIN_BASE}/endpoints`)
        this.applyEndpointCatalog(response)
      } catch (error) {
        this.endpoints = []
        this.endpointCatalogCount = 0
        this.endpointBuiltinCount = 0
        this.missingEndpoints = []
        this.outdatedEndpoints = []
        this.handleLoadError(error, silent === true)
      } finally {
        this.endpointLoading = false
      }
    },
    applyEndpointCatalog (response) {
      const value = response || {}
      this.endpoints = value.items || []
      this.endpointCatalogCount = this.number(value.catalogCount || this.endpoints.length)
      this.endpointBuiltinCount = this.number(value.builtinCount || 27)
      this.missingEndpoints = value.missingEndpoints || []
      this.outdatedEndpoints = value.outdatedEndpoints || []
    },
    async syncBuiltinEndpoints () {
      if (this.endpointSyncing || this.endpointLoading || this.endpointSaving) return
      const execute = async () => {
        this.endpointSyncing = true
        try {
          const result = await yuqingApi.post(`${ADMIN_BASE}/endpoints/sync`, {})
          this.applyEndpointCatalog(result)
          const created = this.number(result.createdCount)
          const updated = this.number(result.updatedCount)
          this.$message.success(
            `内置接口同步完成：新增${created}项，更新${updated}项，当前共${this.endpointCatalogCount}项`
          )
        } catch (error) {
          this.$message.error(error.message || '内置接口同步失败')
          await this.loadEndpoints(true)
        } finally {
          this.endpointSyncing = false
        }
      }
      if (this.outdatedEndpointCount > 0) {
        this.$confirm(
          `发现${this.outdatedEndpointCount}项执行契约发生变化。同步后这些接口会恢复为待验证并停用，是否继续？`,
          '同步内置接口',
          {
            confirmButtonText: '确认同步',
            cancelButtonText: '取消',
            type: 'warning'
          }
        ).then(execute).catch(() => undefined)
        return
      }
      await execute()
    },
    async loadOperatorUsers (silent) {
      if (this.operatorUsersLoading || this.operatorUsers.length) return
      this.operatorUsersLoading = true
      try {
        const response = await this.$apiGet(
          'lechun-baseservice/user/getUserListForDorpbox',
          {}
        )
        this.operatorUsers = (response || [])
          .map(user => ({
            userId: String(user.USER_ID || '').trim(),
            userName: String(user.USER_NAME || '').trim()
          }))
          .filter(user => user.userId && user.userName)
      } catch (error) {
        if (!silent) this.$message.error('运营人员列表读取失败，请稍后重试')
      } finally {
        this.operatorUsersLoading = false
      }
    },
    selectOperatorUser (userId) {
      const selected = this.operatorUsers.find(user => user.userId === userId)
      this.callerForm.userName = selected ? selected.userName : ''
    },
    isCallerConfigured (userId) {
      return this.callers.some(caller => caller.userId === userId)
    },
    openCreateCaller () {
      this.editingCallerId = null
      this.creatingCurrentCaller = false
      this.callerForm = emptyCaller()
      this.callerDialogVisible = true
      this.loadOperatorUsers(false)
      this.clearCallerValidation()
    },
    openCreateCurrentCaller () {
      if (!this.currentProfile || !this.currentProfile.userId) return
      this.editingCallerId = null
      this.creatingCurrentCaller = true
      this.callerForm = {
        ...emptyCaller(),
        userId: this.currentProfile.userId,
        userName: this.currentProfile.userName || ''
      }
      this.callerDialogVisible = true
      this.clearCallerValidation()
    },
    openEditCaller (row) {
      this.editingCallerId = row.id
      this.creatingCurrentCaller = false
      this.callerForm = {
        userId: row.userId,
        userName: row.userName,
        authorizedPlatforms: [...(row.authorizedPlatforms || [])],
        dailyLimit: this.number(row.dailyLimit),
        totalLimit: this.number(row.totalLimit),
        enabled: Boolean(row.enabled)
      }
      this.callerDialogVisible = true
      this.clearCallerValidation()
    },
    clearCallerValidation () {
      this.$nextTick(() => {
        if (this.$refs.callerForm) this.$refs.callerForm.clearValidate()
      })
    },
    resetCallerForm () {
      this.editingCallerId = null
      this.creatingCurrentCaller = false
      this.callerForm = emptyCaller()
      this.clearCallerValidation()
    },
    saveCaller () {
      this.$refs.callerForm.validate(async valid => {
        if (!valid || this.callerSaving) return
        this.callerSaving = true
        try {
          const payload = {
            enabled: this.callerForm.enabled,
            authorized_platforms: this.callerForm.authorizedPlatforms,
            daily_limit: this.callerForm.dailyLimit,
            total_limit: this.callerForm.totalLimit
          }
          if (this.editingCallerId) {
            await yuqingApi.put(`${ADMIN_BASE}/callers/${this.editingCallerId}`, payload)
          } else {
            payload.user_id = this.callerForm.userId
            payload.user_name = this.callerForm.userName
            await yuqingApi.post(`${ADMIN_BASE}/callers`, payload)
          }
          this.$message.success(this.editingCallerId ? '运营账号权限已更新' : '运营账号已创建')
          this.callerDialogVisible = false
          await Promise.all([
            this.loadCurrentProfile(true),
            this.loadCallers(true)
          ])
        } catch (error) {
          this.$message.error(error.message || '运营账号保存失败')
        } finally {
          this.callerSaving = false
        }
      })
    },
    resetQuota (row) {
      this.$prompt(
        `将“${row.userName}”的累计已用次数清零，每日用量不会清零。`,
        '重置累计额度',
        {
          confirmButtonText: '确认重置',
          cancelButtonText: '取消',
          inputPlaceholder: '填写重置原因（选填）',
          inputValidator: value => !value || value.length <= 500 || '重置原因不能超过500字'
        }
      ).then(async ({ value }) => {
        try {
          await yuqingApi.post(`${ADMIN_BASE}/callers/${row.id}/quota/reset`, {
            reason: value || ''
          })
          this.$message.success('累计额度已重置')
          await this.loadCallers(true)
        } catch (error) {
          this.$message.error(error.message || '额度重置失败')
        }
      }).catch(() => undefined)
    },
    async updateEndpoint (row, payload) {
      if (this.endpointSaving) return
      this.endpointSaving = row.operationCode
      try {
        const result = await yuqingApi.put(
          `${ADMIN_BASE}/endpoints/${encodeURIComponent(row.operationCode)}/status`,
          payload
        )
        const updated = result.endpoint || {}
        Object.assign(row, updated)
        this.$message.success('代理接口状态已更新')
      } catch (error) {
        this.$message.error(error.message || '代理接口状态更新失败')
        await this.loadEndpoints(true)
      } finally {
        this.endpointSaving = ''
      }
    },
    openSmokeDialog (row) {
      this.smokeEndpoint = row
      this.smokeParameterText = '{}'
      this.smokeParameterError = ''
      this.smokeResult = null
      this.smokeError = null
      this.smokeDialogVisible = true
    },
    resetSmokeDialog () {
      this.smokeEndpoint = null
      this.smokeParameterText = '{}'
      this.smokeParameterError = ''
      this.smokeResult = null
      this.smokeError = null
    },
    smokePayload () {
      let payload
      try {
        payload = JSON.parse(this.smokeParameterText || '{}')
      } catch (error) {
        this.smokeParameterError = 'JSON格式不正确，请检查括号、引号和逗号'
        return null
      }
      if (!payload || Array.isArray(payload) || typeof payload !== 'object') {
        this.smokeParameterError = '请求参数必须是JSON对象'
        return null
      }
      return payload
    },
    async submitSmoke () {
      if (!this.smokeEndpoint || this.smokeSubmitting) return
      const payload = this.smokePayload()
      if (!payload) return
      this.smokeSubmitting = true
      this.smokeResult = null
      this.smokeError = null
      try {
        const operationCode = encodeURIComponent(this.smokeEndpoint.operationCode)
        this.smokeResult = await yuqingApi.post(
          `${ADMIN_BASE}/endpoints/${operationCode}/smoke`,
          payload
        )
        this.$message.success('真实冒烟调用成功，已计1次；请人工核对返回结果')
        await Promise.all([this.loadRecords(), this.loadCallers(true)])
      } catch (error) {
        this.smokeError = {
          message: error.message || '真实冒烟调用失败',
          code: error.code || '',
          requestId: error.requestId || '',
          usage: error.usage || {},
          providerHttpStatus: error.providerHttpStatus,
          providerMessage: error.providerMessage || ''
        }
        this.$message.error(error.message || '真实冒烟调用失败')
        await Promise.all([this.loadRecords(), this.loadCallers(true)])
      } finally {
        this.smokeSubmitting = false
      }
    },
    async markSmokeVerified () {
      if (!this.smokeEndpoint || !this.smokeResult) return
      await this.updateEndpoint(this.smokeEndpoint, { live_status: 'LIVE_VERIFIED' })
      if (this.smokeEndpoint.liveStatus === 'LIVE_VERIFIED') {
        this.$message.success('已标记验证通过；确认准备对运营开放后，可再打开启用开关')
        this.smokeDialogVisible = false
      }
    },
    usageText (used, reserved, limit) {
      const consumed = this.number(used)
      const pending = this.number(reserved)
      const maximum = this.number(limit)
      return `${consumed}${pending ? ` +${pending}预占` : ''} / ${maximum || '不限'}`
    },
    number (value) {
      const result = Number(value)
      return Number.isFinite(result) ? result : 0
    },
    platformLabel (code) {
      return PLATFORM_NAMES[code] || code || '-'
    },
    operationLabel (operationCode) {
      const endpoint = this.endpoints.find(item => item.operationCode === operationCode)
      return (endpoint && endpoint.operationName) || operationCode || '-'
    },
    countReason (row) {
      if (this.number(row && row.chargedCount) > 0) {
        return 'TikHub调用已计入次数'
      }
      if (!row || !row.sentAt) {
        return '请求未发往TikHub'
      }
      if (['TIKHUB_TIMEOUT', 'TIKHUB_NETWORK_ERROR'].includes(row.errorCode)) {
        return '结果无法确认，本次不计次'
      }
      if (row.quotaStatus === 'RELEASED') {
        return 'TikHub明确本次不扣费'
      }
      return '本次未计入调用次数'
    },
    quotaStatusLabel (status) {
      return ({
        RESERVED: '额度已预占',
        RELEASED: '额度已释放',
        CONSUMED: '已计入调用次数'
      })[status] || status || '-'
    },
    errorReason (errorCode) {
      return ({
        TIKHUB_CLIENT_ERROR: 'TikHub拒绝了本次请求',
        TIKHUB_VALIDATION_ERROR: 'TikHub参数校验失败',
        TIKHUB_SERVER_ERROR: 'TikHub服务暂时异常',
        TIKHUB_TIMEOUT: 'TikHub请求超时',
        TIKHUB_NETWORK_ERROR: 'TikHub网络请求失败',
        TIKHUB_EMPTY_RESULT: 'TikHub未返回有效数据',
        TIKHUB_ACCOUNTING_PENDING: '调用次数正在恢复',
        INTERNAL_ERROR: '系统处理异常'
      })[errorCode] || errorCode || '-'
    },
    resultLabel (status) {
      return ({ SUCCESS: '成功', FAILED: '失败', TIMEOUT: '超时' })[status] || status || '-'
    },
    resultType (status) {
      return ({ SUCCESS: 'success', FAILED: 'danger', TIMEOUT: 'warning' })[status] || 'info'
    },
    liveStatusLabel (status) {
      return ({
        LIVE_VERIFIED: '验证通过',
        LIVE_FAILED: '验证失败',
        PENDING: '待验证'
      })[status] || status || '-'
    },
    liveStatusType (status) {
      return ({
        LIVE_VERIFIED: 'success',
        LIVE_FAILED: 'danger',
        PENDING: 'warning'
      })[status] || 'info'
    },
    dateTime (value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      const pad = number => String(number).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
        `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    }
  }
}
</script>

<style scoped>
.operator-settings {
  min-height: 360px;
}

.identity-bar {
  min-height: 58px;
  margin-bottom: 14px;
  padding: 12px 16px;
  border: 1px solid #e4e7ed;
  background: #fafafa;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.identity-content {
  min-width: 0;
}

.identity-heading {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 3px;
}

.identity-label {
  color: #606266;
  font-size: 13px;
  font-weight: 500;
}

.identity-name {
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

.identity-actions {
  flex: 0 0 auto;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(140px, 1fr));
  gap: 12px;
  margin-bottom: 18px;
}

.summary-item {
  padding: 14px 16px;
  border: 1px solid #ebeef5;
  background: #fafafa;
}

.summary-label {
  color: #909399;
  font-size: 12px;
}

.summary-value {
  margin-top: 6px;
  color: #303133;
  font-size: 24px;
  font-weight: 600;
  line-height: 30px;
}

.operator-tabs {
  min-height: 300px;
}

.count-reason {
  margin-top: 4px;
  line-height: 18px;
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 4px 0 16px;
}

.filters,
.record-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.filter-status {
  width: 130px;
}

.date-range {
  width: 390px;
}

.record-filters {
  margin: 4px 0 16px;
}

.record-filters > .el-input,
.record-filters > .el-select {
  width: 170px;
}

.record-filters > .operation-filter {
  width: 210px;
}

.primary-text {
  color: #303133;
  font-weight: 500;
  line-height: 21px;
}

.secondary-text,
.form-help {
  color: #909399;
  font-size: 12px;
  line-height: 19px;
}

.platform-tag {
  margin: 2px 4px 2px 0;
}

.reserved {
  color: #e6a23c;
  font-size: 12px;
}

.danger-action {
  color: #f56c6c;
}

.endpoint-sync-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 4px 0 12px;
  padding: 12px 14px;
  border: 1px solid #ebeef5;
  background: #fafafa;
}

.endpoint-sync-title {
  margin-bottom: 3px;
  color: #303133;
  font-weight: 600;
  line-height: 21px;
}

.endpoint-note {
  margin: 4px 0 16px;
}

.smoke-summary,
.smoke-warning {
  margin-bottom: 16px;
}

.smoke-result {
  margin-top: 16px;
  padding: 14px;
  border: 1px solid #d9ecff;
  background: #f5faff;
}

.smoke-result--error {
  border-color: #fde2e2;
  background: #fef0f0;
}

.smoke-error-message,
.smoke-provider-message,
.smoke-retry-tip {
  margin-top: 8px;
  line-height: 1.6;
  word-break: break-word;
}

.smoke-error-message {
  color: #f56c6c;
  font-weight: 500;
}

.smoke-provider-message {
  color: #606266;
}

.smoke-retry-tip {
  color: #e6a23c;
}

.smoke-result-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  color: #303133;
  font-weight: 500;
}

.smoke-result pre {
  max-height: 280px;
  margin: 10px 0 0;
  padding: 12px;
  overflow: auto;
  background: #fff;
  color: #303133;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-all;
}

.full-width {
  width: 100%;
}

.selected-user {
  min-height: 40px;
  padding: 8px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #f5f7fa;
}

.user-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.user-option small {
  color: #909399;
}

@media (max-width: 1100px) {
  .summary-grid {
    grid-template-columns: repeat(2, minmax(140px, 1fr));
  }

  .toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .identity-bar {
    align-items: flex-start;
    flex-direction: column;
  }

  .date-range {
    width: 100%;
  }
}
</style>

<style>
@media (max-width: 760px) {
  .caller-dialog.el-dialog {
    width: calc(100% - 24px) !important;
    margin-top: 6vh !important;
  }

  .caller-dialog .el-dialog__body {
    max-height: calc(88vh - 130px);
    padding: 18px 16px;
    overflow-y: auto;
  }

  .caller-dialog .el-row > .el-col-12 {
    width: 100%;
  }

  .caller-dialog .el-dialog__footer {
    padding: 12px 16px 16px;
  }
}
</style>
